/**
 * Passo 4 do portão — a senha do inscrito.
 *
 * **Há campo de senha, por decisão do dono (D-10, CLAUDE.md, 2026-09-28).** A
 * conta do inscrito é CPF + senha, fora do Keycloak, e só serve para
 * acompanhar a inscrição. A API guarda só o hash (scrypt) e confere a política
 * de novo; a lista abaixo marca cada requisito enquanto a pessoa digita.
 *
 * Quem já tem conta e acabou de comprovar a identidade pode entrar sem trocar
 * a senha — o botão só aparece nesse caso.
 */

import { Check, Circle, KeyRound, Loader2, LogIn } from 'lucide-react';
import { useId, useState } from 'react';

import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';

import { type FinalidadeDoFluxo } from './api';
import { AvisoDoPasso } from './AvisoDoPasso';
import { CampoDeSenha } from './CampoDeSenha';
import { type PropsDoPassoDaIdentidade } from './maquinaDeIdentidade';
import {
  REQUISITOS_DA_SENHA,
  TAMANHO_MAXIMO_DA_SENHA,
  senhaAtendeAPolitica,
} from './politicaDeSenha';

export function IdentidadeSenha({
  estado,
  acoes,
  finalidade,
}: PropsDoPassoDaIdentidade & { finalidade: FinalidadeDoFluxo }) {
  const id = useId();
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [doServidor, setDoServidor] = useState<string[]>([]);
  const [diferente, setDiferente] = useState(false);

  const contaExistente = estado.fluxo?.contaExistente === true;
  const atende = senhaAtendeAPolitica(senha, estado.cpf);
  const confere = senha === confirmacao;

  async function enviar() {
    setDiferente(false);
    if (!confere) {
      setDiferente(true);
      return;
    }
    const resultado = await acoes.definirSenha(senha, confirmacao);
    if (resultado === 'DIFERENTE') setDiferente(true);
    else setDoServidor(resultado ?? []);
  }

  const titulo =
    finalidade === 'RECUPERACAO' || contaExistente
      ? 'Defina a nova senha'
      : 'Defina a sua senha de acesso';

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <KeyRound aria-hidden="true" className="h-5 w-5 text-primary" />
          {titulo}
        </h3>
        <p className="text-sm text-muted-foreground">
          {contaExistente
            ? 'Você já tem uma senha. Pode escolher uma nova agora — as outras sessões abertas com o seu CPF serão encerradas — ou entrar sem trocar.'
            : 'É com o CPF e esta senha que você volta depois para acompanhar a inscrição.'}
        </p>
      </div>

      <form
        className="space-y-4"
        noValidate
        onSubmit={(evento) => {
          evento.preventDefault();
          if (atende && !estado.ocupado) void enviar();
        }}
      >
        <div className="space-y-2">
          <Label htmlFor={`${id}-senha`}>Senha</Label>
          <CampoDeSenha
            id={`${id}-senha`}
            value={senha}
            onChange={(evento) => setSenha(evento.target.value)}
            autoComplete="new-password"
            maxLength={TAMANHO_MAXIMO_DA_SENHA}
            aria-describedby={`${id}-requisitos`}
          />
        </div>

        <ul id={`${id}-requisitos`} className="space-y-1.5" aria-label="O que a senha precisa ter">
          {REQUISITOS_DA_SENHA.map((requisito) => {
            const ok = requisito.cumprido(senha, estado.cpf);
            return (
              <li
                key={requisito.texto}
                className={
                  ok
                    ? 'flex gap-2 text-sm text-foreground'
                    : 'flex gap-2 text-sm text-muted-foreground'
                }
              >
                {ok ? (
                  <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                ) : (
                  <Circle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                )}
                <span>
                  {requisito.texto}
                  <span className="sr-only">{ok ? ' — atendido' : ' — pendente'}</span>
                </span>
              </li>
            );
          })}
        </ul>

        <div className="space-y-2">
          <Label htmlFor={`${id}-confirmacao`}>Repita a senha</Label>
          <CampoDeSenha
            id={`${id}-confirmacao`}
            value={confirmacao}
            onChange={(evento) => setConfirmacao(evento.target.value)}
            autoComplete="new-password"
            maxLength={TAMANHO_MAXIMO_DA_SENHA}
            aria-invalid={diferente || undefined}
            aria-describedby={diferente ? `${id}-diferente` : undefined}
          />
          {diferente && (
            <p id={`${id}-diferente`} className="text-sm font-medium text-destructive">
              As duas senhas não são iguais. Digite a mesma senha nos dois campos.
            </p>
          )}
        </div>

        {doServidor.length > 0 && (
          <AvisoDoPasso aviso={`A senha ainda não atende: ${doServidor.join('; ')}.`} />
        )}
        <AvisoDoPasso aviso={estado.aviso} />

        <Button type="submit" className="w-full" disabled={!atende || estado.ocupado}>
          {estado.ocupado ? (
            <Loader2 aria-hidden="true" className="animate-spin" />
          ) : (
            <KeyRound aria-hidden="true" />
          )}
          {contaExistente || finalidade === 'RECUPERACAO'
            ? 'Salvar a nova senha'
            : 'Criar senha e continuar'}
        </Button>
      </form>

      {contaExistente && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={estado.ocupado}
          onClick={() => void acoes.entrarSemTrocarSenha()}
        >
          <LogIn aria-hidden="true" />
          Entrar sem trocar a senha
        </Button>
      )}
    </div>
  );
}

export default IdentidadeSenha;
